import React, { useEffect, useState } from "react";
import api from "../../../api/client";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Slider } from "../../../components/ui/slider";
import { formatBRL, formatPct } from "../../../utils/format";
import { AlertTriangle, BadgeDollarSign, Stethoscope } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../../../components/ui/alert";

const Card = ({ label, value, accent, testid, sub }) => (
  <div className="border border-border rounded p-5 bg-muted/20" data-testid={testid}>
    <div className="label-tech">{label}</div>
    <div className={`mt-1 text-2xl font-mono font-medium tracking-tight ${accent || ""}`}>{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

const ChoiceCard = ({ checked, onChange, icon: Icon, title, description, testid }) => (
  <label className={`flex gap-3 rounded-xl border p-4 transition-colors cursor-pointer ${checked ? "border-primary/50 bg-primary/[0.05]" : "border-border hover:bg-muted/20"}`}>
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 size-4" data-testid={testid} />
    <Icon className="mt-0.5 size-5 shrink-0 text-primary" />
    <span>
      <span className="block text-sm font-medium">{title}</span>
      <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{description}</span>
    </span>
  </label>
);

export default function Step3Pricing({ data, setData, calc }) {
  const margem = calc?.margem_pct ?? 0;
  const lowMargin = margem < 20;
  const [cidades, setCidades] = useState([]);

  useEffect(() => {
    api.get("/cidades").then((r) => setCidades(r.data || [])).catch(() => {});
  }, []);

  const onCidade = (id) => {
    const c = cidades.find((x) => x.id === id);
    if (!c) {
      setData({ ...data, cidade_referencia: "" });
      return;
    }
    setData({ ...data, cidade_referencia: c.nome, ajuste_preco_pct: c.ajuste_pct || 0 });
  };
  const selectedCidadeId = (cidades.find((c) => c.nome === data.cidade_referencia) || {}).id || "";

  const setAso = (checked) => setData((current) => ({
    ...current,
    acompanhamento: { ...current.acompanhamento, incluir_aso: checked },
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="wizard-step-3">
      <div>
        <h2 className="text-xl font-medium">Precificação e Margem</h2>
        <p className="text-sm text-muted-foreground">Cálculo recalculado em tempo real conforme você ajusta valores.</p>
      </div>

      {lowMargin && calc && (
        <Alert className="border-destructive/50 bg-destructive/10" data-testid="low-margin-alert">
          <AlertTriangle className="size-4 text-destructive" />
          <AlertTitle className="text-destructive">Margem abaixo de 20%</AlertTitle>
          <AlertDescription>Reveja os valores ou ajuste o desconto para garantir saúde financeira do contrato.</AlertDescription>
        </Alert>
      )}

      {calc && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card testid="calc-custo" label="Custo Total" value={formatBRL(calc.custo_total)} />
          <Card testid="calc-receita" label="Receita Bruta" value={formatBRL(calc.receita_bruta)} />
          <Card testid="calc-desconto" label="Desconto" value={formatBRL(calc.valor_desconto)} sub={`${data.desconto_pct}%`} />
          <Card testid="calc-valor-total" label="Valor Total" value={formatBRL(calc.valor_total)} accent="text-primary" />
          <Card testid="calc-margem-rs" label="Margem (R$)" value={formatBRL(calc.margem_valor)} />
          <Card testid="calc-margem-pct" label="Margem (%)" value={formatPct(calc.margem_pct)} accent={lowMargin ? "text-destructive" : "text-primary"} />
          <Card testid="calc-markup" label="Markup (%)" value={formatPct(calc.markup_pct)} />
          <Card testid="calc-lucro" label="Lucro Bruto" value={formatBRL(calc.lucro_bruto)} accent="text-primary" />
          <Card testid="calc-ticket" label="Ticket Mensal" value={formatBRL(calc.ticket_mensal)} sub={`${data.duracao_meses} meses`} />
          <Card testid="calc-parcela" label="Valor por Parcela" value={formatBRL(calc.valor_parcela)} sub={`${data.num_parcelas}x`} />
        </div>
      )}

      <section className="rounded-xl border border-border p-5 space-y-4" data-testid="commercial-presentation-options">
        <div>
          <h3 className="font-medium">Escopo recorrente e apresentação do investimento</h3>
          <p className="mt-1 text-sm text-muted-foreground">Essas escolhas ficam registradas na proposta e determinam o que entra no cálculo e qual valor recebe maior destaque comercial.</p>
        </div>
        <div className="grid gap-3 lg:grid-cols-2">
          <ChoiceCard
            checked={data.acompanhamento?.incluir_aso !== false}
            onChange={setAso}
            icon={Stethoscope}
            title="Incluir acompanhamento de ASO e eventos de SST/eSocial"
            description="Quando desmarcado, o acompanhamento mensal por funcionário sai do escopo, do cálculo e do PDF. As visitas técnicas permanecem independentes."
            testid="toggle-incluir-aso"
          />
          <ChoiceCard
            checked={(data.destaque_investimento || "parcela") === "parcela"}
            onChange={(checked) => setData({ ...data, destaque_investimento: checked ? "parcela" : "total" })}
            icon={BadgeDollarSign}
            title="Dar maior destaque às parcelas"
            description="Recomendado para contratos recorrentes: mostra a parcela como informação principal e mantém o investimento total visível como referência. Desmarque para destacar o total."
            testid="toggle-destaque-parcela"
          />
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-6 pt-4 border-t border-border">
        <div className="space-y-2">
          <Label className="label-tech">Desconto Global: <span className="font-mono">{data.desconto_pct.toFixed(1)}%</span></Label>
          <Slider min={0} max={50} step={0.5} value={[data.desconto_pct]} onValueChange={(v) => setData({ ...data, desconto_pct: v[0] })} data-testid="step3-desconto-slider" />
          <Input type="number" min="0" max="100" step="0.5" value={data.desconto_pct} onChange={(e) => setData({ ...data, desconto_pct: parseFloat(e.target.value || 0) })} data-testid="step3-desconto-input" />
        </div>
        <div>
          <Label className="label-tech">Número de parcelas</Label>
          <Input type="number" min="1" max="60" value={data.num_parcelas} onChange={(e) => setData({ ...data, num_parcelas: parseInt(e.target.value || 1, 10) })} data-testid="step3-parcelas-input" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 pt-4 border-t border-border">
        <div className="space-y-2">
          <Label className="label-tech">Cidade de referência</Label>
          <select
            value={selectedCidadeId}
            onChange={(e) => onCidade(e.target.value)}
            className="h-9 w-full px-2 rounded border border-input bg-background text-sm"
            data-testid="step3-cidade-select"
          >
            <option value="">Nenhuma (usar % manual)</option>
            {cidades.map((c) => (
              <option key={c.id} value={c.id}>{`${c.nome}${c.uf ? "/" + c.uf : ""} · +${c.ajuste_pct || 0}%`}</option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">Selecionar uma cidade preenche o ajuste de preço abaixo (editável).</p>
        </div>
        <div className="space-y-2">
          <Label className="label-tech">Ajuste de preço (%) <span className="text-muted-foreground font-normal">— incide só sobre a venda</span></Label>
          <Input
            type="number" min="0" max="200" step="1"
            value={data.ajuste_preco_pct ?? 0}
            onChange={(e) => setData({ ...data, ajuste_preco_pct: parseFloat(e.target.value || 0) })}
            data-testid="step3-ajuste-preco-input"
          />
          <p className="text-xs text-muted-foreground">Ex.: 30 → aumenta o valor de venda dos serviços em 30%.</p>
        </div>
      </div>
    </div>
  );
}
